import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { CostConsumeRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, DatePicker, Form, Input, InputNumber, Select, Space, Table, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { useAuthStore } from '../stores/auth';

interface ItemValues {
  materialId: string;
  materialName?: string;
  spec?: string;
  unitId: string;
  unitName?: string;
  batchNo?: string;
  qty: number;
  price: number;
}

interface FormValues {
  warehouseId: string;
  deptId: string;
  deptName?: string;
  accountPeriod?: Dayjs;
  remark?: string;
  items: ItemValues[];
}

/**
 * 科室消耗确认（P7）：扣二级库库存并写成本转移单（CB，trigger=MANUAL），
 * 成本按科室归集，可在「成本转移」页追溯，也作为按消耗出账的依据。
 */
export default function SubstockConsume() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const [form] = Form.useForm<FormValues>();

  const warehouses = useQuery({
    queryKey: ['substock', 'warehouses'],
    queryFn: () => hq.orgStructure.warehouse.list({ whKind: 'INNER_SUB', status: 'ACTIVE' }),
    staleTime: 5 * 60 * 1000
  });

  const materials = useQuery({
    queryKey: ['material', 'options'],
    queryFn: () => hq.material.list({ size: 500 }),
    staleTime: 10 * 60 * 1000
  });

  // 科室以机构主数据为准：手填 dept_id 写错会让成本归集挂到别的科室
  const depts = useQuery({
    queryKey: ['substock', 'dept-options'],
    queryFn: () => hq.orgStructure.dept.list({}),
    staleTime: 10 * 60 * 1000
  });

  const materialOptions = (materials.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.materialName ?? row.id}${row.spec ? ` / ${row.spec}` : ''}${row.materialCode ? `（${row.materialCode}）` : ''}`,
    material: row
  }));

  const warehouseOptions = (warehouses.data ?? []).map((row) => ({
    value: row.id,
    label: `${row.whName}${row.deptName ? `（${row.deptName}）` : ''}`,
    warehouse: row
  }));

  const pickMaterial = (index: number, materialId: string) => {
    const picked = materialOptions.find((option) => option.value === materialId)?.material;
    if (!picked) return;
    form.setFieldValue(['items', index, 'materialName'], picked.materialName ?? undefined);
    form.setFieldValue(['items', index, 'spec'], picked.spec ?? undefined);
    form.setFieldValue(['items', index, 'unitId'], picked.baseUnitId);
    form.setFieldValue(['items', index, 'unitName'], picked.unitName ?? undefined);
    form.setFieldValue(['items', index, 'price'], picked.referencePrice ?? 0);
  };

  const consume = useMutation({
    mutationFn: (payload: CostConsumeRequest) => hq.consumption.consume(payload),
    onSuccess: async (result) => {
      message.success(`消耗确认成功：${result.bizNo}`);
      await queryClient.invalidateQueries({ queryKey: ['substock'] });
      await queryClient.invalidateQueries({ queryKey: ['cost'] });
      navigate('/cost');
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '消耗确认失败')
  });

  const onFinish = (values: FormValues) => {
    const pickedWarehouse = warehouseOptions.find((option) => option.value === values.warehouseId)?.warehouse;
    const deptId = values.deptId || pickedWarehouse?.deptId || profile?.deptId || '';
    if (!deptId) {
      message.error('请填写消耗科室（二级库未挂载科室时需要手工指定）');
      return;
    }
    consume.mutate({
      warehouseId: values.warehouseId,
      deptId,
      deptName: values.deptName ?? pickedWarehouse?.deptName ?? null,
      accountPeriod: values.accountPeriod ? values.accountPeriod.format('YYYY-MM') : dayjs().format('YYYY-MM'),
      remark: values.remark ?? null,
      items: values.items.map((item) => ({
        materialId: item.materialId,
        materialName: item.materialName ?? null,
        spec: item.spec ?? null,
        unitId: item.unitId,
        unitName: item.unitName ?? null,
        batchNo: item.batchNo ?? null,
        qty: item.qty,
        price: item.price
      }))
    });
  };

  return (
    <Card className="page-card" title="消耗确认">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="确认后立即扣减二级库库存，并生成成本转移单（CB）"
        description="批次号留空时按可用库存先进先出扣减；成本按科室归集，供「按消耗出账」结算使用。"
      />
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          accountPeriod: dayjs(),
          deptId: profile?.deptId ?? '',
          items: [{ materialId: '', unitId: '', qty: 1, price: 0 }]
        }}
      >
        <Space size={16} wrap>
          <Form.Item
            name="warehouseId"
            label="二级库（消耗出库仓）"
            rules={[{ required: true, message: '请选择二级库' }]}
            style={{ minWidth: 260 }}
          >
            <Select
              loading={warehouses.isPending}
              options={warehouseOptions}
              placeholder="选择科室二级库"
              onChange={(value: string) => {
                const picked = warehouseOptions.find((option) => option.value === value)?.warehouse;
                if (picked) {
                  form.setFieldValue('deptId', picked.deptId || profile?.deptId || '');
                  form.setFieldValue('deptName', picked.deptName ?? undefined);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="deptId" label="消耗科室" rules={[{ required: true, message: '请选择科室' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="搜索并选择科室"
              loading={depts.isFetching}
              options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
              onChange={(value: string) => {
                // 名称快照由后端按主数据回填，这里只用于表单回显
                form.setFieldValue('deptName', (depts.data ?? []).find((row) => row.id === value)?.deptName ?? undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="accountPeriod" label="核算期间" extra="不能晚于当前月份">
            <DatePicker
              picker="month"
              style={{ width: 160 }}
              disabledDate={(current) => current.isAfter(dayjs(), 'month')}
            />
          </Form.Item>
        </Space>

        <Form.Item name="remark" label="备注">
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>

        <Typography.Title level={5}>消耗明细</Typography.Title>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={fields}
                columns={[
                  {
                    title: '物资',
                    render: (_, field) => (
                      <>
                        <Form.Item
                          name={[field.name, 'materialId']}
                          rules={[{ required: true, message: '请选择物资' }]}
                          style={{ margin: 0 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            loading={materials.isLoading}
                            placeholder="搜索物资名称 / 编码 / 规格"
                            options={materialOptions}
                            onChange={(value: string) => pickMaterial(field.name, value)}
                          />
                        </Form.Item>
                        <Form.Item name={[field.name, 'materialName']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'spec']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'unitId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'unitName']} hidden>
                          <Input />
                        </Form.Item>
                      </>
                    )
                  },
                  {
                    title: '规格 / 单位',
                    width: 190,
                    render: (_, field) => {
                      const row = form.getFieldValue(['items', field.name]) as ItemValues | undefined;
                      return (
                        <Typography.Text type="secondary">
                          {row?.spec ? `${row.spec} · ` : ''}
                          {row?.unitName ?? '-'}
                        </Typography.Text>
                      );
                    }
                  },
                  {
                    title: '批次（可空）',
                    width: 150,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'batchNo']} style={{ margin: 0 }}>
                        <Input placeholder="留空先进先出" />
                      </Form.Item>
                    )
                  },
                  {
                    title: '数量',
                    width: 130,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'qty']}
                        rules={[{ required: true, message: '必填' }]}
                        style={{ margin: 0 }}
                      >
                        <InputNumber min={0.001} precision={3} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '成本单价',
                    width: 130,
                    render: (_, field) => (
                      <Form.Item name={[field.name, 'price']} style={{ margin: 0 }}>
                        <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '',
                    width: 56,
                    render: (_, field) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      />
                    )
                  }
                ]}
              />
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 12 }}
                onClick={() => add({ materialId: '', unitId: '', qty: 1, price: 0 })}
              >
                添加明细行
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" loading={consume.isPending}>
            确认消耗
          </Button>
          <Button onClick={() => navigate('/cost')}>查看成本转移</Button>
        </Space>
      </Form>
    </Card>
  );
}
